import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./occasion.css";

/* ═══════════════════════════════════════════════════════
   API BASE
   ═══════════════════════════════════════════════════════ */
const RAW_API_BASE =
  import.meta.env.VITE_API_URL ||
  import.meta.env.VITE_API_BASE_URL ||
  "https://www.thehexaplay.com";

const normalizeApiBase = (base) => {
  const clean = String(base || "").trim().replace(/\/+$/, "");
  if (!clean) return "/api";
  if (clean.endsWith("/api")) return clean;
  return `${clean}/api`;
};

const API_BASE = normalizeApiBase(RAW_API_BASE);
const API = `${API_BASE}/admin`;
const ORDERS_API = `${API_BASE}/orders`;

// Pricing

const EXTRA_ADULT = 400;
const KID_PRICE = 200;
const GLASS_3D_UNIT_PRICE = 300;

const getAdvanceAmount = () => {
  const raw = import.meta.env.VITE_ADVANCE_AMOUNT;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 700;
};

const ADVANCE_AMOUNT = getAdvanceAmount();
const ADVANCE_PAISE = Math.round(ADVANCE_AMOUNT * 100);

const formatINR = (amount) =>
  `₹${Number(amount || 0).toLocaleString("en-IN")}`;

const DISCOUNT_AMOUNT = 200;

const VALID_COUPONS = [
  "J21ER65",
  "K45SD89",
  "L67QW23",
  "M89ZX56",
  "N12AB78",
  "O34CD90",
  "P56EF12",
  "Q78GH34",
  "R90IJ56",
  "S12KL78",
  "T34MN90",
  "U56OP12",
  "V78QR34",
  "W90ST56",
  "X12UV78",
  "Y34WX90",
  "Z56YZ12",
  "A78BC34",
  "B90DE56",
  "C12FG78",
];

const GOOGLE_SHEET_ENDPOINT =
  "https://script.google.com/macros/s/AKfycbyhUBoOlwQ1018rCi6mmV2MnA-3E2qUG8Hw14fDH8zcG5Rd-ephB38Qp5-dD2OR4H0_/exec";

// ═══════════════════════════════════════════════════════
// LEADCONNECTOR (GoHighLevel) WEBHOOK
// ═══════════════════════════════════════════════════════


const getFirstImageFromItem = (item) => {
  if (!item) return "";
  if (Array.isArray(item.images) && item.images.length > 0)
    return item.images[0];
  if (typeof item.image === "string" && item.image) return item.image;
  if (typeof item.imageUrl === "string" && item.imageUrl)
    return item.imageUrl;
  if (typeof item.img === "string" && item.img) return item.img;
  if (typeof item.thumbnail === "string" && item.thumbnail)
    return item.thumbnail;
  if (typeof item.photo === "string" && item.photo) return item.photo;
  return "";
};

const getImageUrl = (imagePath) => {
  if (!imagePath) return "";
  if (imagePath.startsWith("data:")) return imagePath;
  if (imagePath.startsWith("http")) return imagePath;

  const base = import.meta.env.VITE_API_URL
    ? import.meta.env.VITE_API_URL.replace(/\/api$/, "")
    : "";

  const cleanPath = imagePath.startsWith("/") ? imagePath : `/${imagePath}`;
  return `${base}${cleanPath}`;
};

const normalizeCatalogItem = (item) => ({
  id: item._id || item.id || item.slug || `${item.name}-${Math.random()}`,
  name: item.name || item.title || "Untitled",
  price: Number(item.price) || 0,
  img: getImageUrl(getFirstImageFromItem(item)),
});

const buildFlatOrder = (order) => ({
  timestamp: order.timestamp || "",
  status: order.status || "",
  mode: order.mode || "",
  theater: order.theater || "",
  date: order.date || "",
  timeSlot: order.timeSlot || "",
  name: order.name || "",
  celebrationName: order.celebrationName || "",
  whatsapp: order.whatsapp || "",
  email: order.email || "",
  occasion: order.occasion || "",
  adults: order.adults || 0,
  kids: order.kids || 0,
  extraAdults: order.extraAdults || 0,
  basePrice: order.basePrice || 0,
  fixedDecor: order.fixedDecor || 0,
  extraAdultCharge:
    (order.extraAdults || 0) * (order.extraAdultPrice || EXTRA_ADULT),
  kidsCharge: (order.kids || 0) * (order.kidPrice || KID_PRICE),
  glass3DCost: order.glass3DCost || 0,
  glassesCount: order.glassesCount || 0,
  cakes: Array.isArray(order.cakes)
    ? order.cakes.map((c) => `${c.name} (₹${c.price})`).join(", ")
    : "",
  cakesTotal: Array.isArray(order.cakes)
    ? order.cakes.reduce((a, c) => a + (c.price || 0), 0)
    : 0,
  extraDecors: Array.isArray(order.extraDecors)
    ? order.extraDecors.map((d) => `${d.name} (₹${d.price})`).join(", ")
    : "",
  extraDecorsTotal: Array.isArray(order.extraDecors)
    ? order.extraDecors.reduce((a, d) => a + (d.price || 0), 0)
    : 0,
  gifts: Array.isArray(order.gifts)
    ? order.gifts
        .map(
          (g) =>
            `${g.name} x${g.qty || 1} Nos (₹${
              (g.price || 0) * (g.qty || 1)
            })`
        )
        .join(", ")
    : "",
  giftsTotal: Array.isArray(order.gifts)
    ? order.gifts.reduce((a, g) => a + (g.price || 0) * (g.qty || 1), 0)
    : 0,
  services: Array.isArray(order.services)
    ? order.services.map((s) => `${s.name} (${s.price})`).join(", ")
    : "",
  servicesTotal: Array.isArray(order.services)
    ? order.services.reduce((a, s) => a + (s.price || 0), 0)
    : 0,
  grandTotal: order.total || 0,
  advancePaid: order.advancePaid || ADVANCE_AMOUNT,
  couponCode: order.couponCode || "N/A",
  discount: order.discount || 0,
});

const saveOrderToGoogleSheet = (order) => {
  const flatOrder = buildFlatOrder(order);

  fetch(GOOGLE_SHEET_ENDPOINT, {
    method: "POST",
    mode: "no-cors",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(flatOrder),
  }).catch((err) => console.warn("Google Sheet save failed:", err));
};

// ═══════════════════════════════════════════════════════
// Send order to LeadConnector (GoHighLevel) webhook
// ═══════════════════════════════════════════════════════
const sendOrderToLeadConnector = (order) => {
  const flatOrder = buildFlatOrder(order);

  // Send both flattened fields and the full raw order for flexibility in GHL
  const payload = {
    ...flatOrder,
    // Common contact-friendly aliases for GHL mapping
    full_name: order.name || "",
    phone: order.whatsapp || "",
    email: order.email && order.email !== "N/A" ? order.email : "",
    rawOrder: order,
  };

 
    
   
};

const validateWhatsApp = (number) => number.replace(/\D/g, "").length === 10;
const validateCoupon = (code) =>
  VALID_COUPONS.includes(code.toUpperCase().trim());

const FALLBACK_DATA = {
  occasions: [
    { id: 1, name: "Birthday", price: 0, img: "" },
    { id: 2, name: "Anniversary", price: 0, img: "" },
    { id: 3, name: "Surprise Party", price: 0, img: "" },
  ],
  cakes: [
    { id: 4, name: "Vanilla Cake", price: 900, img: "" },
    { id: 5, name: "Choco Cake", price: 1100, img: "" },
  ],
  decorations: [
    { id: 6, name: "LED Lights", price: 1500, img: "" },
    { id: 7, name: "Balloon Arch", price: 800, img: "" },
  ],
  gifts: [
    { id: 8, name: "Special Bouquet - 12 Roses", price: 750, img: "" },
    { id: 9, name: "Bouquet 7 Roses", price: 450, img: "" },
    { id: 10, name: "Bouquet 10 Rose", price: 600, img: "" },
    { id: 11, name: "Crown & Sash Combo", price: 230, img: "" },
    { id: 12, name: "Bride & Groom Glass Combo", price: 300, img: "" },
    { id: 13, name: "Groom to be Glass", price: 160, img: "" },
    { id: 14, name: "Bride to be Glass", price: 160, img: "" },
    { id: 15, name: "Special Poppers 1pc", price: 80, img: "" },
    { id: 16, name: "Number Candles 1pc", price: 60, img: "" },
    { id: 17, name: "Twisty Candles - 5pcs", price: 70, img: "" },
    { id: 18, name: "Heart Candle - 5pcs", price: 110, img: "" },
    { id: 19, name: "Crown", price: 150, img: "" },
    { id: 20, name: "Event Sash", price: 100, img: "" },
  ],
  services: [
    { id: 21, name: "Event Sash", price: 100, img: "" },
    { id: 22, name: "Photoshoot (30 min)", price: 500, img: "" },
    { id: 23, name: "Fog Entry (1 pot)", price: 350, img: "" },
  ],
};

const OccasionSelection = () => {
  const navigate = useNavigate();

  const [summary, setSummary] = useState({});
  const [userData, setUserData] = useState({ date: "", location: "" });
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [couponSuccess, setCouponSuccess] = useState("");
  const [apiData, setApiData] = useState({
    occasions: [],
    cakes: [],
    decorations: [],
    gifts: [],
    services: [],
  });
  const [loadingData, setLoadingData] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [giftQuantities, setGiftQuantities] = useState({});

  const [form, setForm] = useState({
    name: "",
    celebrationName: "",
    whatsapp: "",
    email: "",
    adults: 4,
    kids: 0,
    glassesCount: 0,
    coupon: "",
    couponApplied: false,
    discount: 0,
  });

  useEffect(() => {
    const saved = localStorage.getItem("currentBookingSummary");
    if (!saved) {
      alert("No theater selected. Redirecting you back.");
      navigate(-1);
      return;
    }

    try {
      const parsed = JSON.parse(saved);
      setSummary(parsed);
      setUserData({
        date: parsed.date || "",
        location: parsed.location || "",
      });

      const storedAdults =
        parsed.peopleState?.adults !== undefined
          ? parsed.peopleState.adults
          : parsed.basePeople || 4;
      const storedKids =
        parsed.peopleState?.kids !== undefined ? parsed.peopleState.kids : 0;

      setForm((prev) => ({
        ...prev,
        adults: storedAdults,
        kids: storedKids,
      }));
    } catch (err) {
      console.error("Invalid booking summary:", err);
      alert("Booking data is invalid. Redirecting back.");
      navigate(-1);
    }
  }, [navigate]);

  const safeSummary = {
    cakes: [],
    extraDecors: [],
    gifts: [],
    services: [],
    basePeople: 4,
    maxPeople: 10,
    extraAdultPrice: EXTRA_ADULT,
    kidPrice: KID_PRICE,
    price: 0,
    is3DRoom: false,
    ...summary,
  };

  useEffect(() => {
    const fetchAllData = async () => {
      setLoadingData(true);

      try {
        const cats = ["occasions", "cakes", "decorations", "gifts", "services"];

        const results = await Promise.all(
          cats.map(async (c) => {
            try {
              const r = await fetch(`${API}/${c}`, {
                headers: { "Content-Type": "application/json" },
              });

              if (!r.ok) return [];

              const data = await r.json();

              if (Array.isArray(data)) return data;
              if (data && Array.isArray(data.data)) return data.data;
              if (data && Array.isArray(data[c])) return data[c];
              if (data && Array.isArray(data.items)) return data.items;

              return [];
            } catch {
              return [];
            }
          })
        );

        const hasAnyData = results.some(
          (arr) => Array.isArray(arr) && arr.length > 0
        );

        if (!hasAnyData) {
          setApiData({
            occasions: FALLBACK_DATA.occasions.map(normalizeCatalogItem),
            cakes: FALLBACK_DATA.cakes.map(normalizeCatalogItem),
            decorations: FALLBACK_DATA.decorations.map(normalizeCatalogItem),
            gifts: FALLBACK_DATA.gifts.map(normalizeCatalogItem),
            services: FALLBACK_DATA.services.map(normalizeCatalogItem),
          });
        } else {
          setApiData({
            occasions: (results[0] || []).map(normalizeCatalogItem),
            cakes: (results[1] || []).map(normalizeCatalogItem),
            decorations: (results[2] || []).map(normalizeCatalogItem),
            gifts: (results[3] || []).map(normalizeCatalogItem),
            services: (results[4] || []).map(normalizeCatalogItem),
          });
        }
      } catch {
        setApiData({
          occasions: FALLBACK_DATA.occasions.map(normalizeCatalogItem),
          cakes: FALLBACK_DATA.cakes.map(normalizeCatalogItem),
          decorations: FALLBACK_DATA.decorations.map(normalizeCatalogItem),
          gifts: FALLBACK_DATA.gifts.map(normalizeCatalogItem),
          services: FALLBACK_DATA.services.map(normalizeCatalogItem),
        });
      } finally {
        setLoadingData(false);
      }
    };

    fetchAllData();
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return "Not Selected";
    const [y, m, d] = dateStr.split("-");
    return `${d}/${m}/${y}`;
  };

  const maxPeople = safeSummary.maxPeople || 10;

  const updatePeople = (type, delta) => {
    const currentAdults =
      type === "adults" ? form.adults + delta : form.adults;
    const currentKids = type === "kids" ? form.kids + delta : form.kids;

    if (type === "adults" && currentAdults < safeSummary.basePeople) {
      alert(`Minimum ${safeSummary.basePeople} adults required`);
      return;
    }

    if (currentAdults < 0 || currentKids < 0) return;

    if (currentAdults + currentKids > maxPeople) {
      alert(`Maximum limit of ${maxPeople} people reached.`);
      return;
    }

    setForm({ ...form, [type]: form[type] + delta });
  };

  const glass3DCost = form.glassesCount * GLASS_3D_UNIT_PRICE;
  const extraAdults = Math.max(0, form.adults - safeSummary.basePeople);
  const extraAdultPriceVal = safeSummary.extraAdultPrice || EXTRA_ADULT;
  const kidPriceVal = safeSummary.kidPrice || KID_PRICE;

  const cakesTotal = safeSummary.cakes.reduce(
    (a, c) => a + (Number(c.price) || 0),
    0
  );
  const decorsTotal = safeSummary.extraDecors.reduce(
    (a, d) => a + (Number(d.price) || 0),
    0
  );

  const giftsList = apiData.gifts || [];

  const giftsTotal = giftsList.reduce((acc, g) => {
    const qty = Number(giftQuantities[g.name] || 0);
    const price = Number(g.price || 0);
    return acc + price * qty;
  }, 0);

  // ✅ Updated: save exact selected quantity for each gift
  const activeGifts = giftsList
    .filter((g) => Number(giftQuantities[g.name] || 0) > 0)
    .map((g) => {
      const qty = Number(giftQuantities[g.name] || 0);
      const price = Number(g.price || 0);

      return {
        name: g.name,
        price,
        qty,
        unitPrice: price,
        lineTotal: price * qty,
      };
    });

  const servicesTotal = safeSummary.services.reduce(
    (a, s) => a + (Number(s.price) || 0),
    0
  );

  const subtotal =
    (safeSummary.price || 0) +
   
    extraAdults * extraAdultPriceVal +
    form.kids * kidPriceVal +
    glass3DCost +
    cakesTotal +
    decorsTotal +
    giftsTotal +
    servicesTotal;

  const total = Math.max(0, subtotal - form.discount);

  const validateStep1 = () => {
    const errors = {};

    if (!form.name.trim()) errors.name = "Name is required";
    if (!form.celebrationName.trim())
      errors.celebrationName = "Celebrant name is required";

    if (!form.whatsapp.trim()) {
      errors.whatsapp = "WhatsApp number is required";
    } else if (!validateWhatsApp(form.whatsapp)) {
      errors.whatsapp = "Invalid WhatsApp number (10 digits)";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const nextStep = () => {
    if (step === 1 && !validateStep1()) return;

    if (step === 2 && !safeSummary.occasion) {
      alert("Please select an occasion");
      return;
    }

    if (window.innerWidth <= 900) {
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
    }

    setStep((s) => Math.min(s + 1, 6));
  };

  const prevStep = () => {
    if (step === 1) navigate(-1);
    else setStep(step - 1);
  };

  const selectOccasion = (item) => {
    setSummary((p) => ({ ...p, occasion: item.name }));

    setTimeout(() => {
      setStep(3);
      if (window.innerWidth <= 900)
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, 400);
  };

  const toggleItem = (type, item) => {
    setSummary((prev) => {
      const list = prev[type] || [];
      const exists = list.some((i) => i.name === item.name);

      return exists
        ? { ...prev, [type]: list.filter((i) => i.name !== item.name) }
        : {
            ...prev,
            [type]: [...list, { name: item.name, price: item.price }],
          };
    });
  };

  const removeItem = (type, name) => {
    if (type === "gifts") {
      setGiftQuantities((prev) => ({ ...prev, [name]: 0 }));
    } else {
      setSummary((p) => ({
        ...p,
        [type]: (p[type] || []).filter((i) => i.name !== name),
      }));
    }
  };

  const incGiftQty = (giftName) => {
    setGiftQuantities((prev) => ({
      ...prev,
      [giftName]: Number(prev[giftName] || 0) + 1,
    }));
  };

  const decGiftQty = (giftName) => {
    setGiftQuantities((prev) => {
      const cur = Number(prev[giftName] || 0);
      if (cur <= 0) return prev;

      return {
        ...prev,
        [giftName]: cur - 1,
      };
    });
  };

  const buildOrder = (mode) => ({
    mode,
    theater: safeSummary.theater,
    theaterId: safeSummary.theaterId,
    rawDate: userData.date,
    date: formatDate(userData.date),
    timeSlot: safeSummary.slot,
    basePrice: safeSummary.price,
    
    occasion: safeSummary.occasion,
    name: form.name,
    celebrationName: form.celebrationName,
    whatsapp: form.whatsapp,
    email: form.email || "N/A",
    adults: form.adults,
    kids: form.kids,
    extraAdults,
    extraAdultPrice: extraAdultPriceVal,
    kidPrice: kidPriceVal,
    glass3DCost,
    glassesCount: form.glassesCount,
    total,
    advancePaid: ADVANCE_AMOUNT,
    cakes: safeSummary.cakes,
    extraDecors: safeSummary.extraDecors,
    gifts: activeGifts,
    services: safeSummary.services,
    timestamp: new Date().toISOString(),
    paymentStatus: "PENDING",
    status: "Advance Paid",
    couponCode: form.coupon ? form.coupon.toUpperCase() : "N/A",
    couponApplied: form.couponApplied,
    discount: form.discount,
  });

  const applyCoupon = () => {
    const trimmed = form.coupon.trim();

    if (!trimmed) {
      setCouponError("Please enter a coupon code");
      setCouponSuccess("");
      return;
    }

    if (trimmed.length !== 7) {
      setCouponError("Coupon code must be 7 characters");
      setCouponSuccess("");
      return;
    }

    if (!validateCoupon(trimmed)) {
      setCouponError("Invalid coupon code");
      setCouponSuccess("");
      return;
    }

    setForm((prev) => ({
      ...prev,
      couponApplied: true,
      discount: DISCOUNT_AMOUNT,
    }));

    setCouponSuccess(`✓ Coupon applied! ₹${DISCOUNT_AMOUNT} discount added.`);
    setCouponError("");
  };

  const handlePayAdvance = async (type = "advance") => {
    if (isSubmitting) return;

    if (!safeSummary.occasion) return alert("Please select an occasion");
    if (!form.name || !form.whatsapp)
      return alert("Please fill your name & WhatsApp");
    if (!safeSummary.theaterId)
      return alert("Theater ID missing — please re-select your theater.");
    if (!userData.date)
      return alert("Booking date missing — please re-select your date.");
    if (!safeSummary.slot)
      return alert("Time slot missing — please re-select your slot.");

    setIsSubmitting(true);
    setSubmitError(null);

    const order = buildOrder(type);

    try {
      const orderRes = await fetch(ORDERS_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(order),
      });

      if (!orderRes.ok) {
        const errData = await orderRes.json().catch(() => ({}));
        throw new Error(errData.message || "Failed to save booking to server");
      }

      const orderData = await orderRes.json();
      const bookingId = orderData._id || orderData.orderId || orderData.bookingId;

      if (!bookingId) {
        throw new Error("Server did not return a Booking ID");
      }

      saveOrderToGoogleSheet(order);

      // 🔔 Send lead/order to LeadConnector (GoHighLevel) webhook
      sendOrderToLeadConnector({ ...order, bookingId });

      try {
        const payRes = await fetch(`${API_BASE}/payment/initiate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ bookingId, amount: ADVANCE_PAISE }),
        });

        const payData = await payRes.json().catch(() => ({}));

        if (payRes.ok && payData.success && payData.redirectUrl) {
          try {
            sessionStorage.setItem(
              `__phonepe_order_${bookingId}`,
              JSON.stringify({
                bookingId,
                merchantOrderId: payData.merchantOrderId,
              })
            );
          } catch (_) {}

          setSheetOpen(false);
          window.location.href = payData.redirectUrl;
          return;
        } else {
          throw new Error(
            payData.message || "Payment gateway failed to initiate."
          );
        }
      } catch (payErr) {
        console.warn("⚠️ Payment initiation failed:", payErr.message);
        setSubmitError(
          `Booking saved as Pending, but online payment failed: ${payErr.message}. Our team will contact you.`
        );
        setIsSubmitting(false);
        navigate(`/booking-status?bookingId=${bookingId}&status=pending_error`);
        return;
      }
    } catch (err) {
      console.error("❌ Booking save failed:", err.message);
      setSubmitError(err.message);
      setIsSubmitting(false);
      alert(`Booking failed: ${err.message}`);
    }
  };

  const SummaryRow = ({
    label,
    value,
    remove,
    highlight = false,
    mobile = false,
  }) => {
    const handleRowClick = () => {
      if (mobile && remove) remove();
    };

    return (
      <div
        className={`summary-row ${remove ? "removable" : ""} ${
          mobile && remove ? "mobile-row-clickable" : ""
        }`}
        onClick={handleRowClick}
        style={mobile && remove ? { cursor: "pointer" } : {}}
      >
        <span className="row-label">{label}</span>

        <div className="row-right">
          <span className={`row-value ${highlight ? "row-value-hl" : ""}`}>
            {value}
          </span>

          {remove && (
            <button
              className={`remove-btn ${mobile ? "remove-btn-mobile" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                remove();
              }}
              title="Remove"
              type="button"
            >
              ✕
            </button>
          )}
        </div>
      </div>
    );
  };

  const SummaryContent = ({ mobile = false }) => (
    <>
      <div className={`summary-body ${mobile ? "mobile-summary-body" : ""}`}>
        <SummaryRow
          label="Theater"
          value={safeSummary.theater}
          highlight={mobile}
        />
        <SummaryRow
          label="Date"
          value={formatDate(userData.date)}
          highlight={mobile}
        />
        <SummaryRow
          label="Time Slot"
          value={safeSummary.slot}
          highlight={mobile}
        />
        <SummaryRow label="Base Price" value={`₹${safeSummary.price}`} />
        

        {form.glassesCount > 0 && (
          <SummaryRow
            label={`🥽 3D Glasses (${form.glassesCount})`}
            value={`₹${glass3DCost}`}
            highlight={mobile}
          />
        )}

        {safeSummary.occasion && (
          <SummaryRow
            label="Occasion"
            value={safeSummary.occasion}
            highlight={mobile}
          />
        )}

        {extraAdults > 0 && (
          <SummaryRow
            label={`Extra Adults (${extraAdults})`}
            value={`₹${extraAdults * extraAdultPriceVal}`}
          />
        )}

        {form.kids > 0 && (
          <SummaryRow
            label={`Kids (${form.kids})`}
            value={`₹${form.kids * kidPriceVal}`}
          />
        )}

        {safeSummary.cakes.map((c) => (
          <SummaryRow
            key={c.name}
            label={c.name}
            value={`₹${c.price}`}
            remove={() => removeItem("cakes", c.name)}
            mobile={mobile}
          />
        ))}

        {safeSummary.extraDecors.map((d) => (
          <SummaryRow
            key={d.name}
            label={d.name}
            value={`₹${d.price}`}
            remove={() => removeItem("extraDecors", d.name)}
            mobile={mobile}
          />
        ))}

        {activeGifts.map((g) => (
          <SummaryRow
            key={g.name}
            label={`${g.name}${g.qty > 1 ? ` ×${g.qty} Nos` : ""}`}
            value={`₹${Number(g.lineTotal || g.price * g.qty).toLocaleString(
              "en-IN"
            )}`}
            remove={() => removeItem("gifts", g.name)}
            mobile={mobile}
          />
        ))}

        {safeSummary.services.map((s) => (
          <SummaryRow
            key={s.name}
            label={s.name}
            value={`₹${s.price}`}
            remove={() => removeItem("services", s.name)}
            mobile={mobile}
          />
        ))}

        {form.discount > 0 && (
          <div className="summary-row discount-row">
            <span className="row-label">Coupon Discount</span>
            <span className="row-value discount-value">-₹{form.discount}</span>
          </div>
        )}
      </div>

      {!mobile && (
        <>
          <div className="summary-divider" />

          <div className="summary-total-bar">
            <span>Grand Total</span>
            <span className="total-price">₹{total.toLocaleString()}</span>
          </div>

          

          {submitError && (
            <div className="submit-error-box">⚠️ {submitError}</div>
          )}

          <div className="summary-actions">
            <button
              className="confirm-btn"
              disabled={isSubmitting}
              onClick={() => handlePayAdvance("advance")}
              type="button"
            >
              {isSubmitting
                ? "Processing…"
                : `Pay Advance ${formatINR(ADVANCE_AMOUNT)}`}
            </button>
          </div>
        </>
      )}
    </>
  );

  return (
    <>
      <div className="occasion-layout">
        <div className="occasion-main-content">
          <div className="step-progress">
            <div className="progress-track">
              <div
                className="progress-fill"
                style={{ width: `${((step - 1) / 5) * 100}%` }}
              />
            </div>
            <span className="step-label">Step {step} of 6</span>
          </div>

          <h2 className="step-title">
            {step === 1 && "Your Details"}
            {step === 2 && "Choose Occasion"}
            {step === 3 &&
              `Choose Cake${
                safeSummary.cakes.length ? ` (${safeSummary.cakes.length})` : ""
              }`}
            {step === 4 &&
              `Extra Decoration${
                safeSummary.extraDecors.length
                  ? ` (${safeSummary.extraDecors.length})`
                  : ""
              }`}
            {step === 5 &&
              `Gifts${activeGifts.length ? ` (${activeGifts.length})` : ""}`}
            {step === 6 &&
              `Services${
                safeSummary.services.length
                  ? ` (${safeSummary.services.length})`
                  : ""
              }`}
          </h2>

          <div className="step-content" key={step}>
            {loadingData ? (
              <div className="loading-state">Loading…</div>
            ) : (
              <>
                {step === 1 && (
                  <div className="form-glass">
                    <div className="form-grid">
                      <div className="input-wrapper">
                        <input
                          placeholder="Your Name *"
                          value={form.name}
                          onChange={(e) =>
                            setForm({ ...form, name: e.target.value })
                          }
                          className={validationErrors.name ? "input-error" : ""}
                        />
                        {validationErrors.name && (
                          <span className="error-text">
                            {validationErrors.name}
                          </span>
                        )}
                      </div>

                      <div className="input-wrapper">
                        <input
                          placeholder="Name of the Celebrant *"
                          value={form.celebrationName}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              celebrationName: e.target.value,
                            })
                          }
                          className={
                            validationErrors.celebrationName
                              ? "input-error"
                              : ""
                          }
                        />
                        {validationErrors.celebrationName && (
                          <span className="error-text">
                            {validationErrors.celebrationName}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="form-grid">
                      <div className="input-wrapper">
                        <input
                          placeholder="WhatsApp Number *"
                          value={form.whatsapp}
                          onChange={(e) =>
                            setForm({ ...form, whatsapp: e.target.value })
                          }
                          maxLength="10"
                          className={
                            validationErrors.whatsapp ? "input-error" : ""
                          }
                        />
                        {validationErrors.whatsapp && (
                          <span className="error-text">
                            {validationErrors.whatsapp}
                          </span>
                        )}
                      </div>

                      <input
                        placeholder="Email (Optional)"
                        value={form.email}
                        onChange={(e) =>
                          setForm({ ...form, email: e.target.value })
                        }
                      />
                    </div>

                    <h4 className="people-heading">Guest Count</h4>

                    <div className="people-grid">
                      <Counter
                        label="Adults"
                        value={form.adults}
                        minus={() => updatePeople("adults", -1)}
                        plus={() => updatePeople("adults", 1)}
                      />
                      <Counter
                        label="Kids"
                        value={form.kids}
                        minus={() => updatePeople("kids", -1)}
                        plus={() => updatePeople("kids", 1)}
                      />
                    </div>

                    <div className="people-grid" style={{ marginTop: "16px" }}>
                      <div className="counter">
                        <span>3D Glass</span>
                        <div className="counter-controls">
                          <button
                            type="button"
                            onClick={() =>
                              setForm((prev) => ({
                                ...prev,
                                glassesCount: Math.max(
                                  0,
                                  prev.glassesCount - 1
                                ),
                              }))
                            }
                          >
                            −
                          </button>
                          <b>{form.glassesCount}</b>
                          <button
                            type="button"
                            onClick={() =>
                              setForm((prev) => ({
                                ...prev,
                                glassesCount: prev.glassesCount + 1,
                              }))
                            }
                          >
                            +
                          </button>
                        </div>
                      </div>

                      <div
                        className="coupon-input-wrapper"
                        style={{
                          position: "relative",
                          display: "flex",
                          flexDirection: "column",
                          gap: "6px",
                        }}
                      >
                        <div
                          className="coupon-input-group"
                          style={{ display: "flex", gap: "8px", height: "100%" }}
                        >
                          <input
                            placeholder="Coupon Code (7 digits)"
                            value={form.coupon}
                            onChange={(e) => {
                              setForm({ ...form, coupon: e.target.value });
                              setCouponError("");
                              setCouponSuccess("");
                            }}
                            maxLength="7"
                            disabled={form.couponApplied}
                            className={
                              form.couponApplied ? "coupon-applied" : ""
                            }
                            style={{
                              flex: 1,
                              padding: "14px 16px",
                              background: "#fbfcff",
                              borderRadius: "12px",
                              border: "2px solid #eef0f5",
                              fontSize: "15px",
                              outline: "none",
                              textTransform: "uppercase",
                              letterSpacing: "2px",
                            }}
                          />

                          <button
                            className="apply-coupon-btn"
                            onClick={applyCoupon}
                            disabled={form.couponApplied}
                            type="button"
                          >
                            {form.couponApplied ? "✓ Applied" : "Apply"}
                          </button>
                        </div>

                        {couponError && (
                          <span className="error-text">{couponError}</span>
                        )}
                        {couponSuccess && (
                          <span className="coupon-success">
                            {couponSuccess}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <Grid
                    data={apiData.occasions}
                    selectedItems={[safeSummary.occasion || ""]}
                    onSelect={selectOccasion}
                  />
                )}

                {step === 3 && (
                  <Grid
                    data={apiData.cakes}
                    selectedItems={safeSummary.cakes.map((c) => c.name)}
                    onSelect={(i) => toggleItem("cakes", i)}
                  />
                )}

                {step === 4 && (
                  <Grid
                    data={apiData.decorations}
                    selectedItems={safeSummary.extraDecors.map((d) => d.name)}
                    onSelect={(i) => toggleItem("extraDecors", i)}
                  />
                )}

                {step === 5 && (
                  <div className="gifts-grid">
                    {giftsList.map((g) => {
                      const qty = Number(giftQuantities[g.name] || 0);
                      const hasQty = qty > 0;

                      return (
                        <div
                          key={g.id}
                          className={`gift-card ${hasQty ? "has-qty" : ""}`}
                        >
                          {hasQty && (
                            <div className="gift-selected-count-badge">
                              ×{qty}
                            </div>
                          )}

                          <img
                            src={
                              g.img ||
                              "https://images.unsplash.com/photo-1519748771451-a94c596fad67?w=200&h=200&fit=crop"
                            }
                            alt={g.name}
                            className="gift-card-img"
                            loading="lazy"
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src =
                                "https://images.unsplash.com/photo-1519748771451-a94c596fad67?w=200&h=200&fit=crop";
                            }}
                          />

                          <div className="gift-card-name">{g.name}</div>

                          <div className="gift-card-price-badge">
                            ₹{Number(g.price || 0).toLocaleString("en-IN")}
                          </div>

                          {hasQty && (
                            <div className="gift-card-selected-info">
                              Selected: <b>{qty}</b> Nos
                            </div>
                          )}

                          <div className="gift-qty-control">
                            <button
                              type="button"
                              className="gift-qty-btn"
                              onClick={() => decGiftQty(g.name)}
                              disabled={qty <= 0}
                            >
                              −
                            </button>

                            <span className="gift-qty-num">
                              {String(qty).padStart(2, "0")}
                            </span>

                            <button
                              type="button"
                              className="gift-qty-btn"
                              onClick={() => incGiftQty(g.name)}
                            >
                              +
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {step === 6 && (
                  <Grid
                    data={apiData.services}
                    selectedItems={safeSummary.services.map((s) => s.name)}
                    onSelect={(i) => toggleItem("services", i)}
                  />
                )}
              </>
            )}
          </div>

          <div
  style={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "35px",
    paddingTop: "20px",
    borderTop: "1px solid #e5e7eb",
  }}
>
  <button
    onClick={prevStep}
    type="button"
    style={{
      background: "#ffffff",
      color: "#2563eb",
      border: "1.5px solid #2563eb",
      borderRadius: "10px",
      padding: "11px 24px",
      minWidth: "130px",
      height: "46px",
      fontSize: "15px",
      fontWeight: "600",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
      transition: "all .3s ease",
      letterSpacing: ".3px",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = "#2563eb";
      e.currentTarget.style.color = "#fff";
      e.currentTarget.style.transform = "translateY(-2px)";
      e.currentTarget.style.boxShadow =
        "0 8px 20px rgba(37,99,235,.25)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = "#fff";
      e.currentTarget.style.color = "#2563eb";
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow =
        "0 4px 12px rgba(0,0,0,.08)";
    }}
  >
    ← Back
  </button>

  {step < 6 && (
    <button
      onClick={nextStep}
      type="button"
      style={{
        background: "linear-gradient(135deg,#2563eb,#1d4ed8)",
        color: "#fff",
        border: "none",
        borderRadius: "10px",
        padding: "11px 24px",
        minWidth: "130px",
        height: "46px",
        fontSize: "15px",
        fontWeight: "600",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
        boxShadow: "0 4px 12px rgba(37,99,235,.25)",
        transition: "all .3s ease",
        letterSpacing: ".3px",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow =
          "0 8px 20px rgba(37,99,235,.35)";
        e.currentTarget.style.background =
          "linear-gradient(135deg,#1d4ed8,#1e40af)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow =
          "0 4px 12px rgba(37,99,235,.25)";
        e.currentTarget.style.background =
          "linear-gradient(135deg,#2563eb,#1d4ed8)";
      }}
    >
      Next →
    </button>
  )}
</div>
        </div>

        <div className="occasion-summary">
          <h3 className="summary-title">Booking Summary</h3>
          <SummaryContent mobile={false} />
        </div>
      </div>

      <div className="mobile-summary-bar">
        <div className="mobile-bar-total">
          <span className="mobile-bar-label">Total</span>
          <span className="mobile-bar-amount">₹{total.toLocaleString()}</span>
        </div>

        <div className="mobile-bar-actions">
          <button
            className="mobile-view-btn"
            onClick={() => setSheetOpen(true)}
            type="button"
          >
            View Details
          </button>

          <button
            className="mobile-pay-btn"
            disabled={isSubmitting}
            onClick={() => handlePayAdvance("advance")}
            type="button"
          >
            {isSubmitting ? "…" : `Pay ${formatINR(ADVANCE_AMOUNT)}`}
          </button>
        </div>
      </div>

      <div className={`mobile-summary-overlay${sheetOpen ? " open" : ""}`}>
        <div
          className="mobile-overlay-backdrop"
          onClick={() => setSheetOpen(false)}
        />

        <div className="mobile-sheet">
          <div className="mobile-sheet-header">
            <h3 className="mobile-sheet-title">Booking Summary</h3>
            <button
              className="mobile-sheet-close"
              onClick={() => setSheetOpen(false)}
              type="button"
            >
              ✕
            </button>
          </div>

          <div className="mobile-sheet-items">
            <SummaryContent mobile={true} />
          </div>

          <div className="mobile-sheet-footer">
            <div className="mobile-footer-total-bar">
              <span>Grand Total</span>
              <span className="mobile-footer-total-amount">
                ₹{total.toLocaleString()}
              </span>
            </div>

            <div className="mobile-footer-note">
              * Tap or click on any item to remove it
            </div>

            <div className="mobile-sheet-actions">
              <button
                className="mobile-confirm-btn"
                disabled={isSubmitting}
                onClick={() => handlePayAdvance("advance")}
                type="button"
              >
                {isSubmitting
                  ? "Processing…"
                  : `Pay Advance ${formatINR(ADVANCE_AMOUNT)}`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const Counter = ({ label, value, minus, plus }) => (
  <div className="counter">
    <span>{label}</span>
    <div className="counter-controls">
      <button type="button" onClick={minus}>
        −
      </button>
      <b>{value}</b>
      <button type="button" onClick={plus}>
        +
      </button>
    </div>
  </div>
);

const Grid = ({ data, onSelect, selectedItems }) => (
  <div className="grid">
    {data.map((i) => {
      const selected = selectedItems.includes(i.name);

      return (
        <div
          key={i.id}
          className={`item-card ${selected ? "active" : ""}`}
          onClick={() => onSelect(i)}
        >
          {selected && <div className="selected-badge">✓</div>}

          <img
            src={i.img || "https://via.placeholder.com/200x140?text=No+Image"}
            alt={i.name}
            loading="lazy"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src =
                "https://via.placeholder.com/200x140?text=No+Image";
            }}
          />

          <h4>{i.name}</h4>
          {i.price > 0 && <p className="item-price">₹{i.price}</p>}
        </div>
      );
    })}
  </div>
);

export default OccasionSelection;
